<script setup lang="ts">
const { open, vacancy } = defineProps<{
  /**
   * Whether the modal is open.
   */
  open?: boolean
  /**
   * The vacancy to archive
   */
  vacancy: BaseVacancy
}>()

const emit = defineEmits(['close', 'archived', 'restored'])

const restore = vacancy.status == 'archived'

const { t } = useI18n({
  useScope: 'local',
})

const closeModal = () => {
  emit('close')
}

const isSaving = ref(false)

const router = useRouter()
const toaster = useToaster()

const { handleServerError } = useServerErrorHandler()

const handleArchive = async () => {
  isSaving.value = true
  try {
    // TODO: fix enum import typing
    // const toggledStatus = dearchive ? VacancyStatus.Concept : VacancyStatus.Archived
    const toggledStatus = restore ? 'published' : 'archived'
    // @ts-ignore: TODO: fix typing with layers
    await updateVacancy(vacancy.id, { ...vacancy, status: toggledStatus })

    closeModal()
    toaster.clearAll()
    toaster.show({
      title: t('success', typeLabelContext),
      message: t(restore ? 'restoreSuccessful' : 'archiveSuccessful', typeLabelContext),
      color: 'success',
      icon: 'ph:check',
      closable: true,
    })

    // Basically a refresh
    router.push(`/vacancies/${vacancy.id}`)

    emit(restore ? 'restored' : 'archived')
  }
  catch (error) {
    handleServerError(error)
  }
  finally {
    isSaving.value = false
  }
}

const typeLabelContext = useVacancyTypeName(vacancy)
</script>

<template>
  <BaseModal
    :open="open"
    size="sm"
    :title="t( restore ? 'restoreTitle' : 'archiveTitle', typeLabelContext)"
    @close="closeModal"
  >
    <BaseParagraph
      size="sm"
      class="text-muted-500 dark:text-muted-400"
    >
      {{ t(restore ? 'restoreSubTitle' : 'archiveSubTitle', typeLabelContext) }}
    </BaseParagraph>

    <template #buttons>
      <BaseButton @click="closeModal">
        {{
          $t('buttonActions.cancel')
        }}
      </BaseButton>

      <BaseButton
        color="primary"
        flavor="solid"
        :loading="isSaving"
        :disabled="isSaving"
        @click="handleArchive"
      >
        {{ $t( restore ? 'buttonActions.restore' : 'buttonActions.archive') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<i18n lang="json">
{
  "en": {
    "archiveTitle": "Archive {vacancyTypeName}",
    "archiveSubTitle": "Are you sure you want to archive this {vacancyTypeName}? You can always un-archive it later.",
    "archiveSuccessful": "{vacancyTypeNameCapitalized} archived successfully",
    "restoreTitle": "Restore {vacancyTypeName}",
    "restoreSubTitle": "Are you sure you want to restore this {vacancyTypeName}?",
    "restoreSuccessful": "{vacancyTypeNameCapitalized} restored successfully"
  },
  "nl": {
    "archiveTitle": "{vacancyTypeNameCapitalized} archiveren",
    "archiveSubTitle": "Weet je zeker dat je deze {vacancyTypeName} wilt archiveren? Je kunt de vacature later nog de-archiveren.",
    "archiveSuccessful": "{vacancyTypeNameCapitalized} succesvol gearchiveerd.",
    "restoreTitle": "{vacancyTypeNameCapitalized} herstellen",
    "restoreSubTitle": "Weet je zeker dat je deze {vacancyTypeName} uit het archief wil halen?",
    "restoreSuccessful": "{vacancyTypeNameCapitalized} succesvol hersteld."
  }
}
</i18n>
